"use client";

import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { devSectionAttr } from "@/lib/dev/section-attr";
import { useCcSectionReveal } from "@/lib/use-cc-section-reveal";
import { CcSeoWorkflowConnect } from "./CcSeoWorkflowConnect";
import { CcSeoWorkflowInsights } from "./CcSeoWorkflowInsights";
import { CcSeoWorkflowAdPerformance } from "./CcSeoWorkflowAdPerformance";
import { CcSeoWorkflowCampaign } from "./CcSeoWorkflowCampaign";
import { CcSeoWorkflowScale } from "./CcSeoWorkflowScale";
import { CcSeoWorkflowTrack } from "./CcSeoWorkflowTrack";
import {
  getSeoWorkflowSectionCopy,
  SEO_WORKFLOW_DEFAULT_CARDS,
  type SeoWorkflowCardsCopy,
} from "./seo-workflow-copy";

export type CcSeoWorkflowProps = {
  /** Section `id` and dev overlay key; defaults to `seo-workflow`. */
  sectionId?: string;
  eyebrow?: string;
  title?: string;
  description?: string;
  /** Card titles, descriptions and mock UI strings. Defaults to SEO demo copy (English). */
  cards?: SeoWorkflowCardsCopy;
};

const DEFAULT_SECTION = getSeoWorkflowSectionCopy("en");

export function CcSeoWorkflow({
  sectionId = "seo-workflow",
  eyebrow = DEFAULT_SECTION.eyebrow,
  title = DEFAULT_SECTION.title,
  description = DEFAULT_SECTION.description,
  cards = SEO_WORKFLOW_DEFAULT_CARDS,
}: CcSeoWorkflowProps) {
  const { ref, withRevealClassName } = useCcSectionReveal({ staggerChildren: true });

  return (
    <section
      ref={ref}
      className={withRevealClassName("cc-section cc-seo-workflow")}
      id={sectionId}
      data-cc-nav="light"
      {...devSectionAttr(sectionId)}
    >
      <div className="cc-container cc-seo-workflow__container">
        <header className="cc-seo-workflow__header">
          <CcSanjayaEyebrow>{eyebrow}</CcSanjayaEyebrow>
          <h2 className="cc-seo-workflow__title">{title}</h2>
          <p className="cc-seo-workflow__description">{description}</p>
        </header>

        <div className="cc-seo-workflow__grid">
          <CcSeoWorkflowConnect copy={cards.connect} />
          <CcSeoWorkflowTrack copy={cards.track} />
          <CcSeoWorkflowInsights copy={cards.insights} />
          {cards.campaign ? (
            <CcSeoWorkflowCampaign copy={cards.campaign} />
          ) : cards.adPerformance ? (
            <CcSeoWorkflowAdPerformance copy={cards.adPerformance} />
          ) : cards.scale ? (
            <CcSeoWorkflowScale copy={cards.scale} />
          ) : null}
        </div>
      </div>
    </section>
  );
}
